import React, { useState } from "react";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
  type SelectChangeEvent,
} from "@mui/material";
import { unstable_useId as useId } from "@mui/material/utils";
import { ANDatePicker } from "@answersnow/pkg-theme";
import uniqueId from "lodash/uniqueId";

import {
  MARKER_LABEL_MAX,
  type MarkerFieldsProps,
  type MarkerLinkMode,
  type MarkerSessionOption,
} from "./MarkerDialog.types";

/** Shared look for every field label in the marker form. */
export const FIELD_LABEL_SX = {
  typography: "caption",
  fontWeight: 500,
  color: "text.secondary",
} as const;

/** Shared rounded-input styling for the form's text fields. */
const ROUNDED_INPUT_SX = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } } as const;

interface SessionSelectProps {
  /** Field label — differs per marker type (see the Figma spec). */
  label: string;
  /** Currently selected session id, or "" when none is picked yet. */
  value: string;
  /** Sessions to choose from. */
  sessions: MarkerSessionOption[];
  /** Called with the newly selected session id. */
  onChange: (sessionId: string) => void;
  /** Locks the select, e.g. when editing a marker that can't change session. */
  disabled?: boolean;
}

/**
 * Session dropdown the marker is anchored to. Disabled when the target has no
 * recorded sessions.
 *
 * The visible label is the select's accessible name (via `labelId`), so screen
 * readers announce the same wording that's on screen — which differs per marker
 * type — rather than a generic hardcoded string.
 *
 * @param props
 * @param props.label    - Field label above the select
 * @param props.value    - Selected session id
 * @param props.sessions - Available sessions
 * @param props.onChange - Selection handler
 * @param [props.disabled] - Locks the select regardless of the sessions available
 */
export const SessionSelect: React.FC<SessionSelectProps> = ({
  label,
  value,
  sessions,
  onChange,
  disabled = false,
}) => {
  // Per-instance ids — several dialogs can render at once (e.g. Storybook
  // docs). On React 17 MUI's hook assigns the id after mount, so skip the
  // "-label" suffix until it exists rather than emit "undefined-label".
  const id = useId();
  const labelId = id ? `${id}-label` : undefined;

  return (
    <FormControl fullWidth>
      <FormLabel id={labelId} sx={FIELD_LABEL_SX}>
        {label}
      </FormLabel>
      <Select
        displayEmpty
        id={id}
        labelId={labelId}
        value={value}
        onChange={(event: SelectChangeEvent) => onChange(event.target.value)}
        size="small"
        disabled={disabled || sessions.length === 0}
        renderValue={(selected) =>
          selected ? (
            (sessions.find((session) => session.id === selected)?.label ?? selected)
          ) : (
            <Typography variant="body.small" color="text.disabled">
              Select a session
            </Typography>
          )
        }
        sx={{ borderRadius: "10px" }}
      >
        {sessions.map((session) => (
          <MenuItem key={session.id} value={session.id}>
            {session.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface MarkerDateFieldProps {
  /** Field label — differs per marker type. */
  label: string;
  /** Selected date, or null when none is picked yet. */
  value: Date | null;
  /** Called with the newly picked date. */
  onChange: (date: Date | null) => void;
  /** Locks the picker, e.g. while editing a marker. */
  disabled?: boolean;
}

/**
 * Date picker for a marker linked to a specific date. The visible label is the
 * input's accessible name.
 *
 * @param props
 * @param props.label    - Field label above the picker
 * @param props.value    - Selected date
 * @param props.onChange - Change handler
 * @param [props.disabled] - Locks the picker
 */
export const MarkerDateField: React.FC<MarkerDateFieldProps> = ({
  label,
  value,
  onChange,
  disabled = false,
}) => {
  // Created on the first render, unlike MUI's useId on React 17: ANDatePicker
  // subscribes to "another picker opened" with the id it mounts with, and would
  // close itself right after opening if that id were undefined.
  const [id] = useState(() => uniqueId("marker-date-"));
  const labelId = `${id}-label`;

  return (
    <FormControl fullWidth>
      <FormLabel id={labelId} sx={FIELD_LABEL_SX}>
        {label}
      </FormLabel>
      <ANDatePicker
        id={id}
        value={value}
        onChange={(date: Date | null) => onChange(date)}
        format="MM/dd/yyyy"
        disabled={disabled}
        slotProps={{
          textField: {
            fullWidth: true,
            size: "small",
            inputProps: { "aria-labelledby": labelId },
            sx: ROUNDED_INPUT_SX,
          },
          // The default in-place popper is clipped by the dialog's scrollable
          // content (only the month header shows); portal it to the body instead.
          popper: { disablePortal: false },
        }}
      />
    </FormControl>
  );
};

/** Copy for the link mode controls — differs per marker type. */
export interface MarkerAnchorCopy {
  /** Radio group label, e.g. "Link external event to". */
  linkLabel: string;
  /** Session select label. */
  sessionLabel: string;
  /** Date picker label. */
  dateLabel: string;
}

/** Radio options, in display order. */
const LINK_MODE_OPTIONS: { value: MarkerLinkMode; label: string }[] = [
  { value: "session", label: "A previously recorded session" },
  { value: "date", label: "A specific date" },
];

type MarkerAnchorFieldsProps = Pick<MarkerFieldsProps, "values" | "sessions" | "onFieldChange"> & {
  /** Type-specific labels. */
  copy: MarkerAnchorCopy;
};

/**
 * Where a marker sits on the graph: a radio to link it to a recorded session or
 * a specific date, then the session select or the date picker for that choice.
 *
 * @param props
 * @param props.values        - Current form values
 * @param props.sessions      - Sessions available to anchor the marker to
 * @param props.onFieldChange - Field setter owned by the dialog shell
 * @param props.copy          - Type-specific labels
 */
export const MarkerAnchorFields: React.FC<MarkerAnchorFieldsProps> = ({
  values,
  sessions,
  onFieldChange,
  copy,
}) => {
  // Created on the first render so the radio group is named from the start.
  const [linkLabelId] = useState(() => uniqueId("marker-link-label-"));

  return (
    <Stack gap={7}>
      <FormControl>
        <FormLabel id={linkLabelId} sx={FIELD_LABEL_SX}>
          {copy.linkLabel}
        </FormLabel>
        <RadioGroup
          aria-labelledby={linkLabelId}
          value={values.linkMode}
          onChange={(event) => onFieldChange("linkMode", event.target.value as MarkerLinkMode)}
        >
          {LINK_MODE_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio size="small" />}
              label={option.label}
              componentsProps={{ typography: { variant: "body.small" } }}
            />
          ))}
        </RadioGroup>
      </FormControl>

      {values.linkMode === "session" ? (
        <SessionSelect
          label={copy.sessionLabel}
          value={values.sessionId}
          sessions={sessions}
          onChange={(sessionId) => onFieldChange("sessionId", sessionId)}
        />
      ) : (
        <MarkerDateField
          label={copy.dateLabel}
          value={values.markerDate}
          onChange={(markerDate) => onFieldChange("markerDate", markerDate)}
        />
      )}
    </Stack>
  );
};

interface LabelFieldProps {
  /** Current label text. */
  value: string;
  /** Placeholder example, specific to the marker type. */
  placeholder: string;
  /** Called with the new text, already capped at `MARKER_LABEL_MAX`. */
  onChange: (label: string) => void;
  /**
   * Guidance shown below the field. When omitted the character counter is
   * shown instead — phase lines use the hint, external events the counter.
   */
  helperText?: string;
  /** Focus target after a "create more" save. */
  inputRef?: React.Ref<HTMLInputElement>;
}

/**
 * Short label drawn next to the marker on the graph. Capped at
 * `MARKER_LABEL_MAX` characters, which is also the backend's limit.
 *
 * @param props
 * @param props.value       - Current label text
 * @param props.placeholder - Type-specific example text
 * @param props.onChange    - Change handler
 * @param [props.helperText] - Hint shown instead of the character counter
 * @param [props.inputRef]   - Ref to the underlying input, used to restore focus
 */
export const LabelField: React.FC<LabelFieldProps> = ({
  value,
  placeholder,
  onChange,
  helperText,
  inputRef,
}) => (
  <FormControl margin="none" fullWidth>
    <FormLabel sx={FIELD_LABEL_SX}>Label</FormLabel>
    <TextField
      value={value}
      onChange={(event) => onChange(event.target.value.slice(0, MARKER_LABEL_MAX))}
      placeholder={placeholder}
      size="small"
      inputRef={inputRef}
      inputProps={{ "aria-label": "Marker label", maxLength: MARKER_LABEL_MAX }}
      sx={ROUNDED_INPUT_SX}
    />
    <Typography variant="body.caption" color="text.secondary" sx={{ mt: 1 }}>
      {helperText ?? `${value.length}/${MARKER_LABEL_MAX} characters`}
    </Typography>
  </FormControl>
);

interface NotesFieldProps {
  /** Current notes text. */
  value: string;
  /** Called with the new text. */
  onChange: (notes: string) => void;
}

/**
 * Optional free-text notes about the marker. Identical for both marker types.
 *
 * @param props
 * @param props.value    - Current notes text
 * @param props.onChange - Change handler
 */
export const NotesField: React.FC<NotesFieldProps> = ({ value, onChange }) => (
  <FormControl fullWidth>
    <FormLabel sx={FIELD_LABEL_SX}>Notes (optional)</FormLabel>
    <TextField
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder="Additional details about this event..."
      multiline
      minRows={2}
      inputProps={{ "aria-label": "Marker notes" }}
      sx={ROUNDED_INPUT_SX}
    />
  </FormControl>
);
